import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PAGE_REGISTRY, pagesByWorkspace, type PageWorkspace } from '@/lib/page-registry';

interface ViewerPagePickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Checkbox tree letting an admin pick which sidebar pages a Viewer user can
 * see. Two columns (Inventory / Production), each with select-all. Caller
 * controls when to render this — typically only when the role select is set
 * to "Viewer".
 *
 * Page IDs come from `apps/frontend/src/lib/page-registry.ts` — the same
 * source the sidebar uses to filter nav items at render time.
 */
export default function ViewerPagePicker({ value, onChange }: ViewerPagePickerProps) {
  const { t } = useTranslation('nav');
  const selected = new Set(value);

  const togglePage = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Preserve registry order so the stored array is stable across edits.
    onChange(PAGE_REGISTRY.filter((p) => next.has(p.id)).map((p) => p.id));
  };

  const toggleWorkspace = (workspace: PageWorkspace) => {
    const inWorkspace = pagesByWorkspace(workspace).map((p) => p.id);
    const allSelected = inWorkspace.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allSelected) {
      inWorkspace.forEach((id) => next.delete(id));
    } else {
      inWorkspace.forEach((id) => next.add(id));
    }
    onChange(PAGE_REGISTRY.filter((p) => next.has(p.id)).map((p) => p.id));
  };

  return (
    <div className="space-y-2">
      <Label>Visible pages</Label>
      <p className="text-xs text-muted-foreground">
        Pick the sidebar pages this Viewer can see. Pages without a check are hidden from their nav. Backend permissions still prevent any write actions.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border rounded-md p-3">
        {(['inventory', 'production'] as const).map((workspace) => {
          const pages = pagesByWorkspace(workspace);
          const allSelected = pages.every((p) => selected.has(p.id));
          const someSelected = pages.some((p) => selected.has(p.id));
          return (
            <div key={workspace} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {workspace === 'inventory' ? 'Inventory' : 'Production'}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => toggleWorkspace(workspace)}
                >
                  {allSelected ? 'Clear' : someSelected ? 'Select all' : 'Select all'}
                </Button>
              </div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {pages.map((page) => (
                  <div key={page.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`viewer-page-${page.id}`}
                      checked={selected.has(page.id)}
                      onCheckedChange={() => togglePage(page.id)}
                    />
                    <Label
                      htmlFor={`viewer-page-${page.id}`}
                      className="text-sm font-normal leading-none cursor-pointer"
                    >
                      {t(page.labelKey, { defaultValue: page.id })}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
